<template>
  <!--
    Total XP column: header, then [XP pill | Quests-progress pill], then the
    full-width "Invite friends … reward" row pill.
  -->
  <div class="flex min-w-0 flex-1 flex-col gap-3">
    <span class="text-[15px] font-semibold uppercase leading-[22px] text-text">
      {{ t("channel.profileCard.totalXpEarned") }}
    </span>

    <div class="flex flex-row items-center gap-3">
      <span
        class="inline-flex h-8 shrink-0 items-center gap-1 rounded-full bg-surface px-2 py-1 text-[13px] font-semibold leading-[20px] text-text"
      >
        <GoldCoinIcon class="size-4 shrink-0" />
        <span data-testid="channel-profile-card-xp"
          >{{ formattedXp }}{{ t("channel.profileCard.xpSuffix") }}</span
        >
      </span>

      <!--
        Quests-completed counter — a PROGRESS-BAR pill. Outer rounded-full pill
        with a 2px light frame; an absolute brand-fill track fills the
        completion fraction behind the text; the label is in a frosted
        bg-white/80 inner badge. Fraction derived by parsing the "X/Y"
        completedLabel prop; defaults to 0 when unparseable.
      -->
      <span
        class="relative inline-flex h-8 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-full border-2 border-surface bg-surface"
        data-testid="channel-profile-card-quests"
      >
        <!-- Brand-fill progress track — width is the completion fraction -->
        <span
          class="absolute inset-y-0 left-0 rounded-full bg-fill-brand"
          :style="{ width: questsTrackWidth }"
          aria-hidden="true"
        />
        <!-- Frosted label badge -->
        <span
          class="relative z-10 rounded-full bg-surface/80 px-2 py-0.5 text-[13px] font-semibold leading-5 text-text"
        >
          <slot name="quests-counter">
            <span v-if="completedLabel">{{ completedLabel }}</span>
            {{ t("channel.profileCard.questsCompleted") }}
          </slot>
        </span>
      </span>
    </div>

    <!-- Invite-friends reward row: label left, reward badge right. -->
    <button
      type="button"
      class="flex h-[50px] w-full flex-row items-center justify-between gap-2 rounded-full border border-border bg-surface px-2.5 text-left transition-colors hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-brand"
      data-testid="channel-profile-card-invite"
      @click="emit('invite')"
    >
      <span class="px-2.5 text-[15px] font-medium leading-[20px] text-text">
        {{ t("channel.profileCard.inviteFriends") }}
      </span>

      <!-- Reward badge: bg-surface-secondary grey chip + gold coin -->
      <span
        class="inline-flex h-8 shrink-0 items-center gap-1 rounded-full bg-surface-secondary px-2 py-1 text-[13px] font-semibold leading-[20px] text-text"
      >
        <GoldCoinIcon class="size-4 shrink-0" />
        {{
          t("channel.profileCard.inviteFriendsReward", {
            amount: inviteFriendsReward,
          })
        }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import GoldCoinIcon from "./gold-coin-icon.vue";

defineProps<{
  formattedXp: string;
  completedLabel?: string;
  questsTrackWidth: string;
  inviteFriendsReward: string;
}>();

const emit = defineEmits<{
  invite: [];
}>();

const { t } = useI18n();
</script>
